import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { SnAbilityService } from '@lib/ability';
import { SnWidget } from '@lib/api/entities';
import { SnOperationalViewsAPI } from '@lib/api/services';
import { SnFormFieldConfig } from '@lib/form';
import { t } from '@lib/translation';

@Component({
  selector: 'sn-website-iframe-widget',
  templateUrl: './website-iframe-widget.component.html',
  styleUrls: ['./website-iframe-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebsiteIframeWidgetComponent implements OnInit, OnDestroy {
  @Input() public widget: SnWidget;
  @Input() public viewId: string;

  public src: SafeResourceUrl;
  public changeUrl: boolean;
  public changeTitle: boolean = false;
  public form: UntypedFormGroup = new UntypedFormGroup({});
  public formModel: any = {};
  public formFields: SnFormFieldConfig[] = [
    {
      key: 'iframeUrl',
      type: 'input',
      templateOptions: {
        appearance: 'outline',
        floatLabel: 'always',
        placeholder: t('dashboard.operational.widgets.enterUrlPlaceholder'),
        required: true,
        label: t('dashboard.operational.widgets.websiteUrlLabel'),
        type: 'url',
        pattern: 'https://.*',
      },
    },
  ];
  public readonly canChange: boolean;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _cd: ChangeDetectorRef,
    private readonly _operationalViewsAPI: SnOperationalViewsAPI,
    private readonly _ability: SnAbilityService,
    private readonly _sanitizer: DomSanitizer,
  ) {
    this.canChange = this._ability.can({ subject: 'dashboards/widgetobject', action: 'change' });
  }

  public ngOnInit(): void {
    this._checkForUrl();
    this._cd.detectChanges();
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public toggleChangeUrl(): void {
    this.changeUrl = !this.changeUrl;
    this._cd.detectChanges();
  }

  public editTitle(): void {
    this.changeTitle = true;
  }

  public setTitle(value: boolean): void {
    this.changeTitle = value;
    this._cd.detectChanges();
  }

  public setUpdateTitle(value: string): void {
    this.widget.name = value;
    this._cd.detectChanges();
  }

  public submitForm(): void {
    if (!this.changeTitle) {
      this._operationalViewsAPI
        .updateViewWidget(this.viewId, this.widget.id, {
          meta: { iframeUrl: this.form.value.iframeUrl },
        })
        .pipe(takeUntil(this._destroy$))
        .subscribe((widget) => {
          this.widget.meta.iframeUrl = widget.meta.iframeUrl;
          this.src = this._sanitizeUrl(this.widget.meta.iframeUrl);
          this.changeUrl = false;
          this._cd.detectChanges();
        });
    } else {
      this.changeTitle = false;
      this._cd.detectChanges();
    }
  }

  private _sanitizeUrl(url: string): SafeResourceUrl {
    return this._sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  private _checkForUrl(): void {
    if (this.widget.meta.iframeUrl) {
      this.src = this._sanitizeUrl(this.widget.meta.iframeUrl);
      this._cd.detectChanges();
    }
  }
}
